import { isStudySetupStatusKey, type StudySetupMerchantQueue, type StudySetupStatusKey } from './study-setup-lifecycle'
import type { StudySetupSort } from './study-setup-index'
import { DEFAULT_STUDY_SETUP_SORT } from './study-setup-index'
import { shopifyComponentCopy } from '@testfeed/product-language'

export const STUDY_QUEUE_QUERY_KEY = 'status'
export const STUDY_QUERY_KEY = 'q'
export const STUDY_SORT_QUERY_KEY = 'sort'
export const STUDY_AUDIENCE_QUERY_KEY = 'audience'
export const STUDY_TREATMENT_QUERY_KEY = 'treatment'
export const STUDY_PRODUCT_QUERY_KEY = 'product'
export const STUDY_LIFECYCLE_QUERY_KEY = 'lifecycle'
export const STUDY_PAGE_QUERY_KEY = 'page'

export const MERCHANT_QUEUE_FILTERS: Array<{ id: StudySetupMerchantQueue; label: string }> = [
  { id: 'all', label: shopifyComponentCopy.studySetupsPage.allStudies001 },
  { id: 'drafts', label: shopifyComponentCopy.studySetupsPage.needsReview002 },
  { id: 'running', label: shopifyComponentCopy.studySetupsPage.inProgress003 },
  { id: 'results-ready', label: shopifyComponentCopy.studySetupsPage.resultsReady004 },
  { id: 'errors', label: shopifyComponentCopy.studySetupsPage.needsAttention005 },
]

export function queueFromUrl(): StudySetupMerchantQueue {
  const value =
    typeof window === 'undefined' ? null : new URLSearchParams(window.location.search).get(STUDY_QUEUE_QUERY_KEY)
  return MERCHANT_QUEUE_FILTERS.some((filter) => filter.id === value) ? (value as StudySetupMerchantQueue) : 'all'
}

export function listFromUrl(key: string) {
  if (typeof window === 'undefined') return []
  const values = new URLSearchParams(window.location.search).getAll(key)
  return values.filter(Boolean)
}

export function valueFromUrl(key: string) {
  if (typeof window === 'undefined') return ''
  return new URLSearchParams(window.location.search).get(key) ?? ''
}

export function queueUrl(queue: StudySetupMerchantQueue) {
  const url = new URL(window.location.href)
  if (queue === 'all') url.searchParams.delete(STUDY_QUEUE_QUERY_KEY)
  else url.searchParams.set(STUDY_QUEUE_QUERY_KEY, queue)
  return `${url.pathname}${url.search}${url.hash}`
}

export function pageFromUrl(search: string) {
  const page = Number(new URLSearchParams(search).get(STUDY_PAGE_QUERY_KEY))
  return Number.isSafeInteger(page) && page > 0 ? page : 1
}

export function setListInUrl(url: URL, key: string, values: string[]) {
  url.searchParams.delete(key)
  for (const value of values) url.searchParams.append(key, value)
}

export function selectedSortValue(value: string[]): StudySetupSort {
  const candidate = value[0]?.replace(/ (asc|desc)$/, '-$1') as StudySetupSort | undefined
  const allowed: StudySetupSort[] = [
    'product-title-asc',
    'product-title-desc',
    'study-title-asc',
    'study-title-desc',
    'status-asc',
    'treatment-count-asc',
    'treatment-count-desc',
    'created-desc',
    'created-asc',
  ]
  return candidate && allowed.includes(candidate) ? candidate : DEFAULT_STUDY_SETUP_SORT
}

/** Status filter values from the URL. Anything that is not a merchant status key matches no test, so it is dropped. */
export function statusKeysFromUrl(): StudySetupStatusKey[] {
  return listFromUrl(STUDY_LIFECYCLE_QUERY_KEY).filter(isStudySetupStatusKey)
}
